import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ChatProvider } from "@/context/ChatContext";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Sidebar } from "@/components/Sidebar";
import { ReferenceProvider } from "@/context/ReferenceContext";
import { ReferencePanel } from "@/components/ReferencePanel";
import { Chat } from "@/components/chat/Chat";
import UserProvider, { useUser } from "@/context/UserProvider";
import { Toaster } from "@/components/ui/toaster";
import { ToastProvider } from "@/components/ui/toast";
import { MunicipalityProvider } from "@/context/MunicipalityContext";
import { useState, useEffect } from "react";
import { GrantSuiteWorkspace } from "@/components/grant_suite/GrantSuiteWorkspace";
import { PermitSuiteWorkspace } from "@/components/permit_suite/PermitSuiteWorkspace";
import { DevelopmentSuiteWorkspace } from "@/components/development_suite/DevelopmentSuiteWorkspace";
import { TrustSuiteWorkspace } from "@/components/trust_suite/TrustSuiteWorkspace";
import { userHasGrantDiscover, userIsOutsideUser } from "@/lib/grantSuiteAccess";
import { canAccessPermitTab } from "@/lib/permitSuiteAccess";
import { userHasDevelopmentSuite } from "@/lib/developmentSuiteAccess";
import { userHasTrustSuite } from "@/lib/trustSuiteAccess";
import { GrantSuiteNavigationProvider } from "@/context/GrantSuiteNavigationContext";
import { PermitSuiteNavigationProvider } from "@/context/PermitSuiteNavigationContext";
import { isPermitHubSection, tabForPermitHubSection } from "@/components/permit_suite/permitSuiteTabs";
import { ConnectAnalytics } from "@/components/analytics/ConnectAnalytics";
import { ConnectAnalyticsLive } from "@/components/analytics/ConnectAnalyticsLive";
import { StaffReportsAssistant } from "@/components/staff_reports/StaffReportsAssistant";
import { LegislationMonitorWorkspace } from "@/components/legislation_monitor/LegislationMonitorWorkspace";
import { CasePortal } from "@/components/case_portal/CasePortal";
import { CodeLibraryComparePortal } from "@/components/code_library/CodeLibraryComparePortal";
import { CodeLibraryCompareProvider } from "@/context/CodeLibraryCompareContext";
import { MunicipalityResourceLinksManager } from "@/components/municipality_resource_links/MunicipalityResourceLinksManager";
import { FileViewerExplorer } from "@/components/file_viewer/FileViewerExplorer";
import SandboxChatPage from "@/pages/Sandbox/ChatPage";
import OpsSandboxPage from "@/pages/Ops/SandboxPage";
import { LaunchPad } from "@/components/launch_pad/LaunchPad";
import { PlanReviewProvider } from "@/context/PlanReviewContext";
import { StaffReportsProvider } from "@/context/StaffReportsContext";
import { CasePortalProvider } from "@/context/CasePortalContext";
import { SandboxConversationsProvider } from "@/context/SandboxConversationsContext";
import { detectIosSafari } from "@/lib/utils";
import { getStaffReportDraft } from "@/api/staff_reports";
import { useChatContext } from "@/context/ChatContext";
import { useStaffReports } from "@/context/StaffReportsContext";
import { usePlanReview } from "@/context/PlanReviewContext";
import { useCasePortal } from "@/context/CasePortalContext";
import type { ActiveSection, LaunchPadNavigateOptions } from "@/types/appNavigation";

// This will be removed by vite when a production build is created
if (import.meta.env.DEV) {
  // Allows the UI height taking the debug bar into account
  const debugBar = document.getElementById("__debugbar");
  const shadowRoot = document.getElementById("__debugbar-shadow-root")?.shadowRoot;

  const updateHeight = () => {
    const headerHeight = 30 + (shadowRoot?.getElementById("debugbar-header")?.offsetHeight || 0);
    const bodyHeight = shadowRoot?.getElementById("debugbar-body")?.offsetHeight || 0;
    const totalHeight = headerHeight + bodyHeight;

    document.documentElement.style.setProperty("--debug-bar-height", `${totalHeight}px`);
  };

  updateHeight();
  debugBar?.addEventListener("click", updateHeight);
}

// This is so you can leverage React Query for caching and data fetching
// https://tanstack.com/query/v4/docs/framework/react/overview
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});


function ChatPageMain({
  activeSection: requestedSection,
  setActiveSection,
  isIosSafari,
}: {
  activeSection: ActiveSection;
  setActiveSection: React.Dispatch<React.SetStateAction<ActiveSection>>;
  isIosSafari: boolean;
}) {
  const { currentUser } = useUser();
  const canCodeLibrary = Boolean(currentUser?.code_library_access);
  const canGrantDiscover = userHasGrantDiscover(currentUser);
  const canTrustSuite = userHasTrustSuite(currentUser);
  // Outside collaborators (ENG-1820) live on Grants regardless of the shell
  // flag: every other section is a member surface whose API calls would be
  // denied. Derived at render, so no other section ever mounts.
  const outsideUser = userIsOutsideUser(currentUser);
  const activeSection: ActiveSection =
    outsideUser && requestedSection !== "grants" && requestedSection !== "grant_suite"
      ? "grant_suite"
      : requestedSection;
  const { loadConversation, setCurrentKind } = useChatContext();
  const { setSelectedDraft, nextDraftSelectionVersion, getDraftSelectionVersion } = useStaffReports();
  const { setSelectedReviewId } = usePlanReview();
  const { openCase } = useCasePortal();

  const handleLaunchPadNavigate = async (section: ActiveSection, options?: LaunchPadNavigateOptions) => {
    if (options?.chatId != null && options.chatKind) {
      setCurrentKind(options.chatKind);
      await loadConversation(options.chatId, options.chatKind);
    }

    if (options?.staffReportDraftId != null) {
      try {
        const requestVersion = nextDraftSelectionVersion();
        const full = await getStaffReportDraft(options.staffReportDraftId);
        if (getDraftSelectionVersion() === requestVersion) {
          setSelectedDraft(full as Parameters<typeof setSelectedDraft>[0]);
        }
      } catch {
        // navigation still proceeds; user can pick draft in sidebar
      }
    }

    if (options?.planReviewId != null) {
      setSelectedReviewId(options.planReviewId);
    }

    if (options?.caseNumber) {
      openCase(options.caseNumber);
    }

    setActiveSection(section);
  };

  // A user can be routed here (sidebar, launch pad, or an action alert) for a
  // section their team's flags don't actually grant them. Gate on the
  // specific tab the section resolves to, not "has any permitting flag," so
  // this can never mount a module the user doesn't have.
  const showPermitSuite =
    isPermitHubSection(activeSection) && canAccessPermitTab(currentUser, tabForPermitHubSection(activeSection));

  return (
    <GrantSuiteNavigationProvider setActiveSection={setActiveSection}>
      <ToastProvider>
        <Toaster />
      </ToastProvider>
      <div
        className="flex h-screen overflow-hidden"
        style={{ height: isIosSafari ? "calc(var(--vh, 1vh) * 100)" : "100vh" }}
      >
        <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} />

        <main id="main-content" className="flex-1 flex flex-col min-h-0 overflow-hidden outline-none">
          <div className="flex-1 flex min-h-0">
            <div className="flex-1 flex flex-col min-h-0 min-w-0" style={{ marginTop: "var(--chat-top-margin)" }}>
              {activeSection === "dashboard" && <LaunchPad onNavigate={handleLaunchPadNavigate} />}
              {activeSection === "chat" && <Chat />}
              {activeSection === "code_library" && canCodeLibrary && <CodeLibraryComparePortal />}
              {(activeSection === "grants" || activeSection === "grant_suite") && canGrantDiscover && (
                <GrantSuiteWorkspace />
              )}
              {showPermitSuite && <PermitSuiteWorkspace entrySection={activeSection} />}
              {activeSection === "development_suite" && userHasDevelopmentSuite(currentUser) && (
                <DevelopmentSuiteWorkspace />
              )}
              {activeSection === "trust_suite" && canTrustSuite && <TrustSuiteWorkspace />}
              {activeSection === "staff_reports" && <StaffReportsAssistant />}
              {activeSection === "analytics" && <ConnectAnalytics />}
              {activeSection === "analytics_live" && <ConnectAnalyticsLive />}
              {activeSection === "legislation_monitor" && currentUser?.legislation_monitor_access && (
                <LegislationMonitorWorkspace />
              )}
              {activeSection === "case_portal" && currentUser?.case_portal_access && <CasePortal />}
              {activeSection === "municipality_resource_links" &&
                currentUser?.municipality_resource_links_access && <MunicipalityResourceLinksManager />}
              {activeSection === "file_viewer" && currentUser?.file_viewer && <FileViewerExplorer />}
              {activeSection === "sandbox" && currentUser?.sandbox_access && <SandboxChatPage embedded />}
              {activeSection === "sandbox_audit" &&
                currentUser?.sandbox_access &&
                currentUser?.can_access_sandbox_audit && (
                  <OpsSandboxPage embedded />
                )}
            </div>
          </div>
        </main>
      </div>
    </GrantSuiteNavigationProvider>
  );
}

function ChatPageMainWithPermitNav(props: Parameters<typeof ChatPageMain>[0]) {
  return (
    <PermitSuiteNavigationProvider setActiveSection={props.setActiveSection}>
      <ChatPageMain {...props} />
    </PermitSuiteNavigationProvider>
  );
}

function ChatPageContent({
  activeSection,
  setActiveSection,
  isIosSafari,
}: {
  activeSection: ActiveSection;
  setActiveSection: React.Dispatch<React.SetStateAction<ActiveSection>>;
  isIosSafari: boolean;
}) {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <SidebarProvider defaultCollapsed={window.innerWidth < 1024}>
        <CasePortalProvider>
          <MunicipalityProvider>
            <PlanReviewProvider>
              <StaffReportsProvider>
                <ChatProvider>
                  <ReferenceProvider>
                    <SandboxConversationsProvider>
                      <CodeLibraryCompareProvider>
                        <ChatPageMainWithPermitNav
                          activeSection={activeSection}
                          setActiveSection={setActiveSection}
                          isIosSafari={isIosSafari}
                        />
                      </CodeLibraryCompareProvider>
                    </SandboxConversationsProvider>
                  </ReferenceProvider>
                </ChatProvider>
              </StaffReportsProvider>
            </PlanReviewProvider>
          </MunicipalityProvider>
        </CasePortalProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}

function ChatPage() {
  const [activeSection, setActiveSection] = useState<ActiveSection>("dashboard");
  // Detect iOS Safari and set CSS variable for dynamic viewport height
  const isIosSafari = detectIosSafari();
  useEffect(() => {
    if (!isIosSafari) return;
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);
    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, [isIosSafari]);
  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <ChatPageContent activeSection={activeSection} setActiveSection={setActiveSection} isIosSafari={isIosSafari} />
      </UserProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default ChatPage;
